import { Component, OnInit } from '@angular/core'
import { BankTranDate, CreateBankTranDateRequest } from 'src/api/invoice-leaf/invoice-leaf-api.interface'
import { InvoiceLeafApiService } from 'src/api/invoice-leaf/invoice-leaf-api.service'
import { MessageService } from 'src/app/components/message/message.service'
import { ConfirmService } from 'src/app/module/common-parts/confirm/confirm.service'
import { SelectOption } from 'src/app/module/common-parts/select/select.component.interface'
import { ActionTargetInfo, GridActionButton } from 'src/app/module/is-grid/action-grid/action-grid.component.interface'
import { Header, RowSelection } from 'src/app/module/is-grid/base-grid/base-grid.component.interface'

@Component({
  selector: 'app-ura-bank-tran-date',
  templateUrl: './ura-bank-tran-date.component.html',
  styleUrls: ['./ura-bank-tran-date.component.scss']
})
export class UraBankTranDateComponent implements OnInit {
  title = '口座振替日マスタ管理'
  currentYear = new Date().getFullYear()
  selectedFilter: string = `${this.currentYear}`
  yearOptions: SelectOption[] = []
  allRows: BankTranDate[] = []
  rows: BankTranDate[] = []
  addDialogOpen = false
  infoDialogOpen = false
  addDialogRows: BankTranDate[] = []
  columns: Header[] = [
    {
      key: 'no',
      label: 'No.',
      width: '6%',
      style: {
        textAlign: 'center'
      }
    },
    {
      key: 'tranYearMonth',
      label: '年月',
      width: '20%',
      style: {
        textAlign: 'center'
      }
    },
    {
      key: 'actTranDate',
      label: '実振替日',
      width: '20%',
      style: {
        textAlign: 'center'
      }
    }
  ]
  rowSelection = RowSelection.NONE
  actionButtons: GridActionButton[] = [
    {
      label: '追加',
      actionCd: 'add',
      notRowAction: true,
      actionFunction: () => {
        this.openAddDialog()
      }
    },
    {
      label: '削除',
      actionCd: 'delete',
      notRowAction: true,
      actionFunction: () => {
        this.deleteRowsForSelectedYear()
      }
    }
  ]
  loading = false
  sending = false

  constructor(
    private invoiceLeafApiService: InvoiceLeafApiService,
    private messageService: MessageService,
    private confirmService: ConfirmService
  ) {}

  ngOnInit(): void {
    this.getList()
  }

  async getList(): Promise<void> {
    this.loading = true
    this.allRows = []
    this.rows = []
    const ret = await this.invoiceLeafApiService.GetBankTranDates()
    this.loading = false
    if (ret?.resultCode !== 0) {
      return
    }
    this.allRows = ret.data.map((row) => {
      const tranDate = this.toInputDate(row.tranDate)
      return {
        ...row,
        tranDate,
        tranYearMonth: this.toYearMonthLabel(tranDate),
        actTranDate: this.toInputDate(row.actTranDate),
        actionCds: ["delete"],
      }
    })
    this.setYearOptions()
    this.applyFilter()
  }

  onActionCommit(event: ActionTargetInfo): void {
    if (event.actionCd !== 'delete') {
      return
    }
    this.deleteRowsForSelectedYear()
  }

  openAddDialog(): void {
    if (this.hasRegisteredDataInSelectedYear()) {
      this.messageService.warning(`${this.selectedYear}年は既に登録されています`)
      return
    }
    this.addDialogRows = this.buildRowsForYear()
    this.addDialogOpen = true
  }

  resetAddDialogRowsForYear(): void {
    this.addDialogRows = this.buildRowsForYear()
  }

  closeAddDialog(): void {
    this.addDialogOpen = false
  }

  openInfoDialog(): void {
    this.infoDialogOpen = true
  }

  closeInfoDialog(): void {
    this.infoDialogOpen = false
  }

  removeAddModalRow(index: number): void {
    if (this.addDialogRows.length <= 1) {
      return
    }
    this.addDialogRows = this.addDialogRows.filter((_, rowIndex) => rowIndex !== index)
  }

  async commitAddDialog(): Promise<void> {
    if (this.sending) {
      return
    }

    const invalid = this.addDialogRows.find((row) => !row.actTranDate)
    if (invalid) {
      this.messageService.warning('実取引日を入力してください')
      return
    }

    const duplicate = this.hasDuplicateTranDate(this.addDialogRows)
    if (duplicate) {
      this.messageService.warning('取引日に重複があります')
      return
    }

    const payload: CreateBankTranDateRequest[] = this.addDialogRows.map((row) => {
      return {
        tranDate: row.tranDate,
        actTranDate: row.actTranDate
      }
    })

    this.sending = true
    const ret = await this.invoiceLeafApiService.CreateBankTranDates(payload)
    this.sending = false
    if (ret?.resultCode !== 0) {
      return
    }

    this.messageService.success('登録しました')
    this.addDialogOpen = false
    this.getList()
  }

  onFilterChange(): void {
    this.applyFilter()
  }

  removeLocalRow(index: number): void {
    this.rows = this.rows.filter((_, rowIndex) => rowIndex !== index)
  }

  private deleteRowsForSelectedYear(): void {
    const targetRows = this.allRows.filter((row) => this.getDisplayYear(row.tranDate) === this.selectedYear)
    if (!targetRows.length) {
      this.messageService.warning('削除対象がありません')
      return
    }

    const registeredIds = targetRows
      .filter((row) => this.isRegisteredRow(row))
      .map((row) => row.bankTranDateId)
      .filter((bankTranDateId): bankTranDateId is string => !!bankTranDateId)
    const localRows = targetRows.filter((row) => !this.isRegisteredRow(row))

    this.confirmService.confirm(`${this.selectedYear}年の全行を削除します。よろしいですか？`, '', async () => {
      if (localRows.length) {
        this.removeRowsFromLocalState(localRows)
        this.setYearOptions()
        this.applyFilter()
      }

      if (registeredIds.length) {
        await this.deleteRows(registeredIds)
        return
      }

      this.messageService.success('削除しました')
    })
  }

  async deleteRows(ids: string[]): Promise<void> {
    if (!ids?.length || this.sending) {
      return
    }
    this.sending = true
    const ret = await this.invoiceLeafApiService.DeleteBankTranDates(ids)
    this.sending = false
    if (ret?.resultCode !== 0) {
      return
    }
    this.messageService.success('削除しました')
    this.getList()
  }

  async save(): Promise<void> {
    if (this.sending) {
      return
    }

    const newRows = this.rows.filter((row) => !this.isRegisteredRow(row))
    if (!newRows.length) {
      this.messageService.warning('新規登録対象がありません')
      return
    }

    if (!this.rows.length) {
      this.messageService.warning('登録対象がありません')
      return
    }

    const invalid = newRows.find((row) => !row.actTranDate)
    if (invalid) {
      this.messageService.warning('実取引日を入力してください')
      return
    }


    const payload: CreateBankTranDateRequest[] = newRows.map((row) => {
      return {
        tranDate: row.tranDate,
        actTranDate: row.actTranDate
      }
    })

    this.sending = true
    const ret = await this.invoiceLeafApiService.CreateBankTranDates(payload)
    this.sending = false
    if (ret?.resultCode !== 0) {
      return
    }
    this.messageService.success('登録しました')
    this.getList()
  }

  toYearMonthLabel(dateText: string): string {
    if (!dateText) {
      return ''
    }
    return `${dateText.slice(0, 4)}/${dateText.slice(5, 7)}`
  }

  private sortRows(): void {
    this.rows = [...this.rows].sort((a, b) => `${a.tranDate}`.localeCompare(`${b.tranDate}`))
  }

  private applyFilter(): void {
    this.rows = this.allRows.filter((row) => this.getDisplayYear(row.tranDate) === this.selectedYear)
    this.sortRows()
    this.setDisplayRowNumbers()
    this.updateAddButtonDisabled()
  }

  private setDisplayRowNumbers(): void {
    this.rows = this.rows.map((row, index) => {
      return {
        ...row,
        no: index + 1
      } as BankTranDate
    })
  }

  private setYearOptions(): void {
    this.yearOptions = []
    const registeredYears = this.allRows
      .map((row) => this.getDisplayYear(row.tranDate))
      .filter((year) => year > 0)

    const yearSet = new Set<number>()
    yearSet.add(this.currentYear)
    yearSet.add(this.currentYear + 1)

    if (registeredYears.length) {
      const oldestRegisteredYear = Math.min(...registeredYears)
      const latestRegisteredYear = Math.max(...registeredYears)
      const upperYear = Math.max(this.currentYear + 1, latestRegisteredYear)

      for (let year = oldestRegisteredYear; year <= upperYear; year++) {
        yearSet.add(year)
      }
    }

    yearSet.forEach((year) => {
      this.yearOptions.push({ value: `${year}`, label: `${year}年` })
    })
    this.yearOptions = this.yearOptions.sort((a, b) => `${b.value}`.localeCompare(`${a.value}`))

    if (!this.yearOptions.some((option) => option.value === this.selectedFilter)) {
      this.selectedFilter = `${this.currentYear}`
    }
  }

  private updateAddButtonDisabled(): void {
    const addDisabled = this.hasRegisteredDataInSelectedYear()
    this.actionButtons = this.actionButtons.map((button) => {
      if (button.actionCd !== 'add') {
        return button
      }
      return {
        ...button,
        disabled: addDisabled
      }
    })
  }

  private hasRegisteredDataInSelectedYear(): boolean {
    return this.allRows.some((row) => this.isRegisteredRow(row) && this.getDisplayYear(row.tranDate) === this.selectedYear)
  }

  isRegisteredRow(row: BankTranDate): boolean {
    return !!row?.bankTranDateId
  }

  private buildRowsForYear(): BankTranDate[] {
    const rows: BankTranDate[] = []
    const targetYear = this.getTargetYearForAddDialog()
    const startMonthDate = new Date(targetYear, 1, 1)

    for (let month = 0; month < 12; month++) {
      const targetMonth = new Date(startMonthDate.getFullYear(), startMonthDate.getMonth() + month, 1)
      const monthEnd = this.getMonthEndDate(targetMonth.getFullYear(), targetMonth.getMonth())
      rows.push({
        tranDate: this.toInputDate(monthEnd),
        actTranDate: this.toInputDate(this.getDefaultActTranDate(monthEnd)),
        createDatetime: '',
        updateDatetime: ''
      })
    }

    return rows
  }

  private getTargetYearForAddDialog(): number {
    return this.selectedYear
  }

  private removeRowsFromLocalState(targetRows: BankTranDate[]): void {
    if (!targetRows?.length) {
      return
    }

    const targetKeys = new Set(targetRows.map((row) => this.getRowKey(row)))
    this.rows = this.rows.filter((row) => !targetKeys.has(this.getRowKey(row)))
    this.allRows = this.allRows.filter((row) => !targetKeys.has(this.getRowKey(row)))
  }

  private getRowKey(row: BankTranDate): string {
    return [row?.bankTranDateId || '', row?.tranDate || '', row?.actTranDate || ''].join('|')
  }

  private hasDuplicateTranDate(newRows: BankTranDate[]): boolean {
    const existingTranDateSet = new Set(
      this.allRows.filter((row) => this.isRegisteredRow(row)).map((row) => row.tranDate)
    )
    const newTranDateSet = new Set<string>()

    for (const row of newRows) {
      if (existingTranDateSet.has(row.tranDate)) {
        return true
      }
      if (newTranDateSet.has(row.tranDate)) {
        return true
      }
      newTranDateSet.add(row.tranDate)
    }
    return false
  }

  private getYear(dateText: string): number {
    if (!dateText) {
      return 0
    }
    return Number(dateText.slice(0, 4))
  }

  private getMonth(dateText: string): number {
    if (!dateText) {
      return 0
    }
    return Number(dateText.slice(5, 7))
  }

  // 一覧表示・年選択は「2月〜翌1月」を同一年として扱う
  private getDisplayYear(dateText: string): number {
    const year = this.getYear(dateText)
    if (!year) {
      return 0
    }

    const month = this.getMonth(dateText)
    if (month === 1) {
      return year - 1
    }
    return year
  }

  private isAllowedDateByMode(dateText: string): boolean {
    const displayYear = this.getDisplayYear(dateText)
    return displayYear === this.selectedYear
  }

  private get selectedYear(): number {
    return Number(this.selectedFilter)
  }

  private getMonthEndDate(year: number, month: number): Date {
    return new Date(year, month + 1, 0)
  }

  // 銀行カレンダーの厳密判定はできないため、土日だけ翌営業日(月曜)へ寄せる
  private getDefaultActTranDate(tranDate: Date): Date {
    const actDate = new Date(tranDate)
    const day = actDate.getDay()
    if (day === 6) {
      actDate.setDate(actDate.getDate() + 2)
    } else if (day === 0) {
      actDate.setDate(actDate.getDate() + 1)
    }
    return actDate
  }

  private toInputDate(value: string | Date): string {
    if (!value) {
      return ''
    }
    const date = value instanceof Date ? value : new Date(value)
    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    return `${year}-${month}-${day}`
  }
}
